import { useEffect, useRef, useState } from 'react'
import {
  CheckCircle,
  Loader2,
  Mic,
  RefreshCw,
  Sparkles,
  Square,
  Stethoscope,
  X,
} from 'lucide-react'
import {
  postScribeAudio,
  postScribeCommit,
  type ScribeAudioResponse,
  type ScribeEncounterFields,
} from '../api/scribe'
import type { EncounterView } from '../api/clinical'
import { ApiError } from '../lib/api'
import { fmt, t, useLang } from '../lib/i18n'

/**
 * SpeakEncounterDock — the AI-Scribe "Speak encounter" flow.
 *
 * Phases: setup → recording → transcribing → reviewing → committed
 *         (errors: recording / transcribing / committing — all retryable)
 *
 * Audio capture pattern ported from AI-Scribe's src/ui/streaming.js
 * (getUserMedia → capture → stop → release tracks), simplified to a single
 * MediaRecorder blob upload per the pinned contract (multipart POST
 * /patients/{id}/scribe/audio). The provider edits the drafted SOAP note
 * before commit; commit POSTs /scribe/commit and returns the real
 * EncounterView which the parent inserts into the encounters section.
 */

type Phase =
  | { name: 'setup' }
  | { name: 'recording' }
  | { name: 'transcribing' }
  | { name: 'reviewing'; draft: ScribeAudioResponse; noteBody: string }
  | { name: 'committing'; noteBody: string }
  | { name: 'committed'; encounterId: string }
  | { name: 'error'; at: 'recording' | 'transcribing' | 'committing'; message: string }

const ENCOUNTER_TYPES = ['outpatient', 'inpatient', 'emergency', 'telehealth', 'home'] as const

function nowLocalDateTime(): string {
  const d = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

export function SpeakEncounterDock({
  patientId,
  defaultFacilityId,
  open,
  onClose,
  onCommitted,
}: {
  patientId: string
  defaultFacilityId: string
  open: boolean
  onClose: () => void
  onCommitted: (encounter: EncounterView) => void
}) {
  useLang()
  const dir = typeof document !== 'undefined' ? document.documentElement.dir : 'ltr'

  const [phase, setPhase] = useState<Phase>({ name: 'setup' })
  const [encType, setEncType] = useState<string>('outpatient')
  const [facilityId, setFacilityId] = useState(defaultFacilityId)
  const [periodStart, setPeriodStart] = useState(nowLocalDateTime())
  const [practitionerId, setPractitionerId] = useState('')
  const [reasonText, setReasonText] = useState('')
  const [recordingSeconds, setRecordingSeconds] = useState(0)

  const recorderRef = useRef<MediaRecorder | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const timerRef = useRef<number | null>(null)
  const lastFieldsRef = useRef<ScribeEncounterFields | null>(null)
  const lastBlobRef = useRef<Blob | null>(null)
  const lastDraftRef = useRef<{ scribeId: string; fields: ScribeEncounterFields } | null>(null)
  const noteRef = useRef<HTMLTextAreaElement | null>(null)

  // Release mic + timer whenever the dock closes or the component unmounts.
  useEffect(() => {
    if (!open) {
      stopTracks()
      setPhase({ name: 'setup' })
      setRecordingSeconds(0)
    }
    return () => stopTracks()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  function stopTracks() {
    if (timerRef.current !== null) {
      window.clearInterval(timerRef.current)
      timerRef.current = null
    }
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      try { recorderRef.current.stop() } catch { /* already stopped */ }
    }
    recorderRef.current = null
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop())
      streamRef.current = null
    }
  }

  function encounterFields(): ScribeEncounterFields {
    return {
      type: encType,
      facility_id: facilityId.trim(),
      period_start: new Date(periodStart).toISOString(),
      ...(practitionerId.trim() ? { practitioner_id: practitionerId.trim() } : {}),
      ...(reasonText.trim() ? { reason_text: reasonText.trim() } : {}),
    }
  }

  async function startRecording() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      streamRef.current = stream
      chunksRef.current = []
      const recorder = new MediaRecorder(stream)
      recorderRef.current = recorder
      recorder.ondataavailable = (event: BlobEvent) => {
        if (event.data.size > 0) chunksRef.current.push(event.data)
      }
      recorder.start()
      setRecordingSeconds(0)
      timerRef.current = window.setInterval(() => setRecordingSeconds((s) => s + 1), 1000)
      setPhase({ name: 'recording' })
    } catch {
      stopTracks()
      setPhase({ name: 'error', at: 'recording', message: 'mic-denied' })
    }
  }

  function stopRecording() {
    const recorder = recorderRef.current
    if (!recorder || recorder.state === 'inactive') return
    recorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: recorder.mimeType || 'audio/webm' })
      stopTracks()
      lastBlobRef.current = blob
      lastFieldsRef.current = encounterFields()
      void upload(blob, lastFieldsRef.current)
    }
    recorder.stop()
  }

  async function upload(blob: Blob, fields: ScribeEncounterFields) {
    setPhase({ name: 'transcribing' })
    try {
      const draft = await postScribeAudio(patientId, blob, fields)
      const noteBody = draft.structured_note.sections_present
        .map((section) => draft.structured_note.sections[section])
        .filter(Boolean)
        .join('\n\n')
      lastDraftRef.current = { scribeId: draft.scribe_id, fields }
      setPhase({ name: 'reviewing', draft, noteBody })
    } catch (err) {
      const message = err instanceof ApiError ? `HTTP ${err.status}` : 'network'
      setPhase({ name: 'error', at: 'transcribing', message })
    }
  }

  function insertSuggestion(term: string) {
    if (phase.name !== 'reviewing') return
    const el = noteRef.current
    const current = phase.noteBody
    let next: string
    if (el) {
      const start = el.selectionStart ?? current.length
      const end = el.selectionEnd ?? current.length
      next = current.slice(0, start) + (start > 0 && !/\s/.test(current[start - 1]) ? ' ' : '') + term + current.slice(end)
    } else {
      next = current ? `${current}\n${term}` : term
    }
    setPhase({ ...phase, noteBody: next })
  }

  async function commit(noteBody: string) {
    const draft = lastDraftRef.current
    if (!draft) return
    setPhase({ name: 'committing', noteBody })
    try {
      const encounter = await postScribeCommit(patientId, {
        scribe_id: draft.scribeId,
        ...draft.fields,
        note_body: noteBody,
      })
      setPhase({ name: 'committed', encounterId: encounter.id })
      onCommitted(encounter)
    } catch (err) {
      const message = err instanceof ApiError ? `HTTP ${err.status}` : 'network'
      setPhase({ name: 'error', at: 'committing', message })
    }
  }

  if (!open) return null

  const mm = String(Math.floor(recordingSeconds / 60)).padStart(2, '0')
  const ss = String(recordingSeconds % 60).padStart(2, '0')

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-4 sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-label={t('scribe.title')}
      data-testid="scribe-dock"
      dir={dir}
    >
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl border border-stone-200 bg-white p-6 shadow-2xl">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Stethoscope className="h-5 w-5 text-primary-700" />
            <h2 className="text-xl font-semibold text-stone-950">{t('scribe.title')}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('scribe.close')}
            className="grid h-9 w-9 place-items-center rounded-lg border border-stone-200 text-stone-500 transition hover:bg-stone-50 hover:text-stone-800"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {(phase.name === 'setup' || phase.name === 'recording') && (
          <form
            className="grid gap-3 sm:grid-cols-2"
            onSubmit={(e) => { e.preventDefault(); void startRecording() }}
          >
            <label className="grid gap-1.5 text-sm font-medium text-stone-700">
              {t('scribe.encounterType')}
              <select
                value={encType}
                onChange={(e) => setEncType(e.target.value)}
                disabled={phase.name === 'recording'}
                data-testid="scribe-type"
                className="h-11 rounded-lg border border-stone-200 bg-white px-3 text-stone-950 outline-none transition focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"
              >
                {ENCOUNTER_TYPES.map((type) => (
                  <option key={type} value={type}>{t(`enc.type.${type}`)}</option>
                ))}
              </select>
            </label>
            <label className="grid gap-1.5 text-sm font-medium text-stone-700">
              {t('scribe.facility')}
              <input
                value={facilityId}
                onChange={(e) => setFacilityId(e.target.value)}
                disabled={phase.name === 'recording'}
                data-testid="scribe-facility"
                className="h-11 rounded-lg border border-stone-200 bg-white px-3 font-mono text-stone-950 outline-none transition focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"
              />
            </label>
            <label className="grid gap-1.5 text-sm font-medium text-stone-700">
              {t('scribe.periodStart')}
              <input
                type="datetime-local"
                value={periodStart}
                onChange={(e) => setPeriodStart(e.target.value)}
                disabled={phase.name === 'recording'}
                className="h-11 rounded-lg border border-stone-200 bg-white px-3 text-stone-950 outline-none transition focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"
              />
            </label>
            <label className="grid gap-1.5 text-sm font-medium text-stone-700">
              {t('scribe.reason')}
              <input
                value={reasonText}
                onChange={(e) => setReasonText(e.target.value)}
                disabled={phase.name === 'recording'}
                placeholder={t('scribe.reasonHint')}
                className="h-11 rounded-lg border border-stone-200 bg-white px-3 text-stone-950 outline-none transition placeholder:text-stone-400 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"
              />
            </label>

            <div className="mt-2 flex flex-col items-center gap-3 sm:col-span-2">
              {phase.name === 'setup' ? (
                <button
                  type="submit"
                  data-testid="scribe-record"
                  className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-red-600 text-white shadow-lg shadow-red-900/30 transition hover:bg-red-700 active:scale-95"
                  aria-label={t('scribe.startRecording')}
                >
                  <Mic className="h-7 w-7" />
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={stopRecording}
                    data-testid="scribe-stop"
                    className="relative inline-flex h-16 w-16 items-center justify-center rounded-full bg-red-600 text-white shadow-lg shadow-red-900/30 transition hover:bg-red-700 active:scale-95"
                    aria-label={t('scribe.stopRecording')}
                  >
                    <span className="absolute inset-0 animate-ping rounded-full bg-red-400 opacity-40" />
                    <Square className="relative h-6 w-6" />
                  </button>
                  <span className="font-mono text-sm font-semibold text-red-700" role="status">
                    {mm}:{ss} · {t('scribe.recording')}
                  </span>
                </>
              )}
            </div>
          </form>
        )}

        {phase.name === 'transcribing' && (
          <div className="flex flex-col items-center gap-3 py-10" role="status" data-testid="scribe-transcribing">
            <Loader2 className="h-10 w-10 animate-spin text-primary-700" />
            <p className="text-sm font-semibold text-stone-700">{t('scribe.transcribing')}</p>
            <p className="text-xs text-stone-500">{t('scribe.transcribingHint')}</p>
          </div>
        )}

        {(phase.name === 'reviewing' || phase.name === 'committing') && (() => {
          const noteBody = phase.noteBody
          const reviewing = phase.name === 'reviewing'
          const suggestions = reviewing ? phase.draft.suggestions : []
          return (
            <div className="space-y-4" data-testid="scribe-review">
              {reviewing && phase.draft.transcript && (
                <details className="rounded-lg border border-stone-200 bg-stone-50 p-3">
                  <summary className="cursor-pointer text-sm font-semibold text-stone-700">
                    {t('scribe.transcript')}
                  </summary>
                  <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-stone-600" dir="auto">
                    {phase.draft.transcript}
                  </p>
                </details>
              )}

              <label className="grid gap-1.5 text-sm font-medium text-stone-700">
                {t('scribe.noteLabel')}
                <textarea
                  ref={noteRef}
                  value={noteBody}
                  onChange={(e) => reviewing && setPhase({ ...phase, noteBody: e.target.value })}
                  readOnly={!reviewing}
                  rows={10}
                  dir="auto"
                  data-testid="scribe-note"
                  className="w-full rounded-lg border border-stone-200 bg-white px-3 py-2 font-mono text-sm leading-6 text-stone-950 outline-none transition focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100 read-only:bg-stone-50"
                />
              </label>

              {reviewing && suggestions.length > 0 && (
                <div>
                  <div className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-stone-700">
                    <Sparkles className="h-4 w-4 text-primary-700" />
                    {t('scribe.suggestions')}
                  </div>
                  <div className="flex flex-wrap gap-2" data-testid="scribe-suggestions">
                    {suggestions.map((s) => (
                      <button
                        key={`${s.system}:${s.code}`}
                        type="button"
                        onClick={() => insertSuggestion(s.name)}
                        title={`${s.system} ${s.code} · ${(s.score * 100).toFixed(0)}%`}
                        className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-900 transition hover:border-emerald-400 hover:bg-emerald-100 active:scale-95"
                      >
                        {s.name}
                        <span className="font-mono text-[10px] text-emerald-600">{s.code}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex items-center gap-3">
                <button
                  type="button"
                  disabled={!reviewing}
                  onClick={() => void commit(noteBody)}
                  data-testid="scribe-commit"
                  className="inline-flex h-11 items-center gap-2 rounded-lg bg-primary-700 px-4 text-sm font-semibold text-white shadow-md shadow-emerald-900/15 transition hover:bg-primary-800 active:scale-95 disabled:opacity-60"
                >
                  {phase.name === 'committing' ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {t('scribe.committing')}
                    </>
                  ) : (
                    t('scribe.commit')
                  )}
                </button>
                <span className="text-xs text-stone-500">{t('scribe.reviewHint')}</span>
              </div>
            </div>
          )
        })()}

        {phase.name === 'committed' && (
          <div className="flex flex-col items-center gap-3 py-8 text-center" role="status" data-testid="scribe-committed">
            <CheckCircle className="h-12 w-12 text-emerald-600" />
            <p className="text-lg font-semibold text-emerald-900">{t('scribe.committed')}</p>
            <p className="text-sm text-stone-600">{t('scribe.committedHint')}</p>
            <button
              type="button"
              onClick={onClose}
              className="mt-2 inline-flex h-10 items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 text-sm font-semibold text-emerald-900 transition hover:bg-emerald-100"
            >
              {t('scribe.viewEncounter')}
            </button>
          </div>
        )}

        {phase.name === 'error' && (
          <div className="space-y-3" role="alert" data-testid="scribe-error">
            <p className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-800">
              {phase.at === 'recording'
                ? t('scribe.micError')
                : fmt(t('scribe.failed'), { at: t(`scribe.stage.${phase.at}`) })}
              {phase.message !== 'network' && phase.message !== 'mic-denied' ? ` (${phase.message})` : ''}
            </p>
            <button
              type="button"
              onClick={() => {
                if (phase.at === 'transcribing' && lastBlobRef.current && lastFieldsRef.current) {
                  void upload(lastBlobRef.current, lastFieldsRef.current)
                } else if (phase.at === 'committing' && lastDraftRef.current) {
                  const note = noteRef.current?.value ?? ''
                  void commit(note)
                } else {
                  setPhase({ name: 'setup' })
                }
              }}
              className="inline-flex h-10 items-center gap-2 rounded-lg border border-stone-200 bg-white px-4 text-sm font-semibold text-stone-700 transition hover:border-emerald-200 hover:text-emerald-800"
            >
              <RefreshCw className="h-4 w-4" />
              {t('scribe.retry')}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
